// VerifyEmailComponent.js
import React, { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import {
  emailAtom,
  registeredUserIdAtom,
  submitAccessibilityAtom,
} from "@/store/atoms";
import Loader from "@/components/common/Loader";
import { useAccessibility, useI18N } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useDarkMode } from "@/hooks/useDarkMode";
import WideButton from "@/components/common/WideButton";

interface VerifyEmailProps {
  title: string;
  subtitle: string;
}

const VerifyEmailComponent = ({ title, subtitle }: VerifyEmailProps) => {
  const i18n = useI18N();
  const [email] = useAtom(emailAtom);
  const { setScreenIndex } = useAuthModals();

  const [registeredUserId] = useAtom(registeredUserIdAtom);

  const {
    submitAccessibility,
    submitAccessibilityError,
    submitAccessibilityResponse,
    submitAccessibilityStatus,
  } = useAccessibility();

  //get and store the data for entire onBoarding steps
  const [submitData] = useAtom(submitAccessibilityAtom);

  const isDark = useDarkMode();

  const isLoading = submitAccessibilityStatus === "pending";

  useEffect(() => {
    if (submitData && registeredUserId) {
      const dataToSubmit = {
        ...submitData,
        userId: registeredUserId,
      };
      submitAccessibility(dataToSubmit);
    }
  }, [submitData, registeredUserId, submitAccessibility]);

  useEffect(() => {
    if (submitAccessibilityError) {
      console.log(
        "submitAccessibilityError",
        submitAccessibilityError?.message
      );
    }
  }, [submitAccessibilityError]);

  useEffect(() => {
    if (submitAccessibilityResponse) {
      console.log(
        "submitAccessibilityResponse",
        submitAccessibilityResponse?.message
      );

      const userDataCompat = {
        id: submitAccessibilityResponse.user_id,
        ability: "",
        accessibility_need: "",
        email: submitAccessibilityResponse.email,
        gender: "",
        name: submitAccessibilityResponse.name,
        year_of_birth: "",
        level: 1,
      };

      AnalyticsService.setUserProfileProps(
        userDataCompat,
        {
          EmailConfirmed: false,
          HowDoYouHearAboutSociability: "",
        },
        {
          EmailConfirmed: false,
        }
      );
    }
  }, [email, submitAccessibilityResponse]);

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountGotoLoginClickedOnVerification");
    AnalyticsService.trackMx("authPopupCreateAccountGotoLoginClickedOnVerification", {});

    //move to Login
    setScreenIndex(AuthModalScreen.EnterEmail);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  console.log("submitData from OnboardingStep5", submitData);

  return (
    <div
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="flex flex-col rounded-xl bg-white dark:bg-primary-900"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {i18n.get("verifyEmailAddress")}
        </h1>      
      </header>
      <div className="mb-20 px-8 md:px-16 mt-4 flex-1">
        <div className="mt-6">
          <h2
            className="mb-4 text-primary-900 dark:text-primary-100 desktop-h1-xsmall-extra-bold"
            tabIndex={0}
          >
            {title}
          </h2>
          <p
            className="paragraph-medium-medium text-primary-900 dark:text-primary-100"
            tabIndex={0}
          >
            {subtitle}
          </p>
        </div>
        <div>
          <hr className="w-full border-primary-150 dark:border-primary-200 my-6" />
        </div>
        <div aria-label="Model Content">
          {/* content comes here */}
          <div>
            <p
              className="button-regular-semi-bold text-primary-900 dark:text-primary-100 mb-3"
              tabIndex={0}
            >
              {i18n.get("wehaveJustSentAnEmailTo")}
            </p>
            <p
              className="mb-6 button-regular-semi-bold !text-primary-500 dark:!text-primary-300"
              tabIndex={0}
            >
              {email}
            </p>
            <p
              className="paragraph-small-medium text-primary-800 dark:text-primary-150"
              tabIndex={0}
            >
              {i18n.get("clickTheLinkInsideToLogInToSociability")}
            </p>
          </div>
          {/* content comes here */}
        </div>
      </div>
      {isLoading && <Loader />}
      <div className="px-8 md:px-16 mb-16">
        <WideButton onClick={onContinue}>
          {i18n.get("returnToLoginPage")}
        </WideButton>
      </div>
    </div>
  );
};

export default VerifyEmailComponent;
